import React, { useEffect, useState } from "react";
import { Box, Flex, Grid, Input, Label, Select, Switch } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getMoSettings, updateMoSettings, type MoSettings } from "api/MaintenanceOpportunities";
import ServiceTokens from "./ServiceTokens";

/**
 * Maintenance opportunities settings, shown on Company Settings > Leads
 * under the lead settings. Self-contained (own load and save) so the lead
 * settings page is untouched apart from rendering it. Hidden when the
 * caller cannot read the settings (no maintenanceOppView).
 */

const inputSx = { fontFamily: "Poppins", fontSize: "13px", borderColor: "#EBEBEB", borderRadius: "6px" };
const labelSx = { fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "4px", display: "block" };
const hintSx = { fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginBottom: "6px" };

const MaintenanceOpportunitySettingsCard: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<MoSettings | null>(null);
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMoSettings().then((res: any) => {
      if (res?.error) return;
      setForm(res.settings);
      setOwners(res.owners ?? []);
    });
  }, []);

  if (!form) return null;
  const set = <K extends keyof MoSettings>(k: K, v: MoSettings[K]) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    const res: any = await updateMoSettings(form);
    setSaving(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    setForm(res);
    // The nav re-reads the switch on this event (no page reload - standing rule 3).
    window.dispatchEvent(new CustomEvent("mo-settings-changed"));
    toast.add(res.enabled ? "Saved. Opportunities is on." : "Saved. Opportunities is off.", "var(--theme-ui-colors-success)");
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        marginBottom: "24px",
        padding: "24px",
      }}
    >
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" }}>Opportunities</Box>
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "20px" }}>
        Playgrounds we have mulched, queued when they come due for a top-up, so we can find the owner and offer a plan.
      </Box>

      <Flex sx={{ alignItems: "center", gap: "12px", marginBottom: "18px" }}>
        <Box sx={{ width: "44px" }}>
          <Switch checked={form.enabled} onChange={(e: any) => set("enabled", e.target.checked)} />
        </Box>
        <Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800" }}>Turn on Opportunities</Box>
          <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500" }}>Adds Opportunities under Leads. Off hides it everywhere and stops the nightly check.</Box>
        </Box>
      </Flex>

      <Grid columns={[1, 3]} gap="20px">
        <Box>
          <Label sx={labelSx}>Top-up interval (months)</Label>
          <Box sx={hintSx}>How long after an install a site comes due. Changing it re-dates every site without its own interval.</Box>
          <Input type="number" min={1} value={form.intervalMonths} onChange={(e) => set("intervalMonths", Number(e.target.value) || 12)} sx={inputSx} />
        </Box>
        <Box>
          <Label sx={labelSx}>Show sites this many days early</Label>
          <Box sx={hintSx}>The &ldquo;Due soon&rdquo; window. Overdue sites always show.</Box>
          <Input type="number" min={0} value={form.horizonDays} onChange={(e) => set("horizonDays", Number(e.target.value) || 0)} sx={inputSx} />
        </Box>
        <Box>
          <Label sx={labelSx}>Default owner</Label>
          <Box sx={hintSx}>Who leads created from these sites are assigned to.</Box>
          <Select value={form.defaultOwnerId ?? ""} onChange={(e) => set("defaultOwnerId", e.target.value || null)} sx={inputSx}>
            <option value="">The lead default</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </Select>
        </Box>
      </Grid>

      <Flex sx={{ justifyContent: "flex-end", marginTop: "18px" }}>
        <BasicButton type="button" keepText disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</BasicButton>
      </Flex>
      <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "10px" }}>
        Runs itself every night in the 2am Eastern hour on the app&rsquo;s scheduled job. Refresh on the queue runs it on demand.
      </Box>
      {form.enabled && <ServiceTokens />}
    </Box>
  );
};

export default MaintenanceOpportunitySettingsCard;
